import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ViewModuleOutlined from '@mui/icons-material/ViewModuleOutlined';
import TableRowsOutlined from '@mui/icons-material/TableRowsOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import HourglassEmptyOutlined from '@mui/icons-material/HourglassEmptyOutlined';
import FlightOutlined from '@mui/icons-material/FlightOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutlined';
import { EmptyState } from '../components/EmptyState';
import { FilterBar, FilterSelect, PagedFooter, PageHeader } from '../components/ListControls';
import { StatTile } from '../components/StatTile';
import { TabStrip } from '../components/TabStrip';
import { ShipmentCard } from '../features/shipments/ShipmentCard';
import { ShipmentsTable } from '../features/shipments/ShipmentsTable';
import { useShipmentFilters } from '../features/shipments/useShipmentFilters';
import { inTab, SHIPMENT_TABS, STAGE_GROUPS, type Mode } from '../features/shipments/sample';
import { useShipments } from '../features/shipments/useShipments';

const PAGE_SIZE = 8;
const MODES: Mode[] = ['Air', 'FCL', 'LCL', 'Other'];
const ALL_STAGES = STAGE_GROUPS.flatMap((g) => g.stages);

/** The shipments sheet: numbers, tabs, filters, then cards or a table */
export function ShipmentsPage() {
  const navigate = useNavigate();
  const { items: all, loading, error } = useShipments();
  const f = useShipmentFilters(all);
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const customers = [...new Set(all.map((s) => s.customer))].sort();
  const inGroup = (label: string) => all.filter((s) => inTab(label, s)).length;
  const first = (f.page - 1) * PAGE_SIZE;
  const rows = f.shown.slice(first, first + PAGE_SIZE);
  // a number on top opens its tab, narrowed to one stage where it counts just that stage
  const pick =
    (group: string, stage = 'all') =>
    () => {
      f.setGroup(group);
      f.setStage(stage);
    };

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Shipments"
        subtitle="Track and manage your freight operations."
        newLabel="New Shipment"
        onNew={() => void navigate('/shipments/new')}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={view}
          onChange={(_e, v: 'cards' | 'table' | null) => v && setView(v)}
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
        >
          <ToggleButton value="cards" sx={{ textTransform: 'none', gap: 0.5 }}>
            <ViewModuleOutlined fontSize="small" /> Cards
          </ToggleButton>
          <ToggleButton value="table" sx={{ textTransform: 'none', gap: 0.5 }}>
            <TableRowsOutlined fontSize="small" /> Table
          </ToggleButton>
        </ToggleButtonGroup>
      </PageHeader>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.25, md: 1.5 },
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(6, minmax(0, 1fr))',
          },
        }}
      >
        <StatTile
          count={inGroup('Active')}
          title="Active Shipments"
          note="In progress"
          icon={<LocalShippingOutlined />}
          tone="info"
          onClick={pick('Active')}
        />
        <StatTile
          count={all.filter((s) => s.stage === 'Rates Requested').length}
          title="Awaiting Rates"
          note="Waiting for agent quotes"
          icon={<RequestQuoteOutlined />}
          tone="error"
          onClick={pick('Inquiry', 'Rates Requested')}
        />
        <StatTile
          count={all.filter((s) => s.stage === 'Quoting').length}
          title="Awaiting Customer"
          note="Quotation sent"
          icon={<HourglassEmptyOutlined />}
          tone="warning"
          onClick={pick('Inquiry', 'Quoting')}
        />
        <StatTile
          count={all.filter((s) => s.stage === 'In Transit').length}
          title="In Transit"
          note="On the way"
          icon={<FlightOutlined />}
          tone="info"
          onClick={pick('On Hand', 'In Transit')}
        />
        <StatTile
          count={inGroup('Needs Attention')}
          title="Needs Attention"
          note="Overdue or issues"
          icon={<ReportProblemOutlined />}
          tone="error"
          onClick={pick('Needs Attention')}
          selected={f.group === 'Needs Attention'}
        />
        <StatTile
          count={inGroup('Arrived')}
          title="Arrived"
          note="Delivered and done"
          icon={<CheckCircleOutline />}
          tone="success"
          onClick={pick('Arrived')}
        />
      </Box>

      <TabStrip
        tabs={SHIPMENT_TABS.map((label) => ({ value: label, count: inGroup(label) }))}
        value={f.group}
        onChange={f.setGroup}
      />

      <FilterBar placeholder="Search shipments (SRB, customer, route, agent…)" search={f.search} onSearch={f.setSearch}>
        <FilterSelect label="Mode" value={f.mode} onChange={f.setMode} options={MODES} />
        <FilterSelect label="Stage" value={f.stage} onChange={f.setStage} options={ALL_STAGES} />
        <FilterSelect label="Customer" value={f.customer} onChange={f.setCustomer} options={customers} />
      </FilterBar>

      {error && <Alert severity="error">{error}</Alert>}
      {rows.length === 0 ? (
        <EmptyState>
          {loading ? 'Loading shipments…' : all.length ? 'No shipments match.' : 'No shipments yet.'}
        </EmptyState>
      ) : view === 'cards' ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))' }}>
          {rows.map((s) => (
            <ShipmentCard key={s.srb} s={s} />
          ))}
        </Box>
      ) : (
        <ShipmentsTable rows={rows} />
      )}

      <PagedFooter noun="shipments" total={f.shown.length} page={f.page} size={PAGE_SIZE} onPage={f.setPage} />
    </Stack>
  );
}
